import { zodResolver } from '@hookform/resolvers/zod';
import { type EntranceExamSchedule, ExamCategorySchema, ExamTypeSchema } from '@shared/types';
import { AlertCircle, ArrowLeft, ExternalLink, History, Plus, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type Resolver, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as z from 'zod';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Schema matching the backend simplified for the form
// We use a flat structure for the form and convert to nested JSON for API
const examFormSchema = z.object({
  name: z.string().min(1, '必須項目です'),
  slug: z
    .string()
    .min(1, 'URLスラグは必須です')
    .regex(/^[a-z0-9-]+$/, '英数字とハイフンのみ使用可能です'),
  academicYear: z.number().int().min(2020),
  examType: ExamTypeSchema,
  category: ExamCategorySchema,
  parentScheduleId: z.string().optional(), // For Makeup exams

  // Dates
  applicationStart: z.string().min(1, '必須'),
  applicationEnd: z.string().min(1, '必須'),
  announcementDate: z.string().optional(),
  procedureDeadline: z.string().optional(),
  paymentDeadline: z.string().optional(),

  // Config: Payment & Docs
  paymentMethods: z.array(z.string()).default([]),
  documents: z
    .array(
      z.object({
        name: z.string().min(1, '必須'),
        url: z.string().url('URL形式で入力'),
      }),
    )
    .default([]),

  // Detailed Dates (Multi-day)
  examDates: z.array(
    z.object({
      id: z.string().optional(),
      date: z.string().min(1, '日付は必須'),
      label: z.string().optional(),
      capacity: z.coerce.number().optional(),
    }),
  ),

  // Config: Concurrent
  allowCrossExam: z.boolean().default(false),
  allowCrossFaculty: z.boolean().default(false),
  maxChoices: z.coerce.number().optional(),
  hasPriorityRanking: z.boolean().default(false),

  // Config: Selection
  subjectSelectionRequired: z.boolean().default(false),
  ageCalculation: z.enum(['AT_REGISTRATION', 'AS_OF_NEXT_APRIL_1']).default('AS_OF_NEXT_APRIL_1'),
  requireQuestionnaire: z.boolean().default(false),
});

type ExamFormValues = z.infer<typeof examFormSchema>;

export default function ExamForm() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = id && id !== 'new';

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [parentCandidates, setParentCandidates] = useState<EntranceExamSchedule[]>([]);

  const form = useForm<ExamFormValues>({
    resolver: zodResolver(examFormSchema) as unknown as Resolver<ExamFormValues>,
    defaultValues: {
      name: '',
      slug: '',
      academicYear: new Date().getFullYear() + 1,
      examType: 'GENERAL',
      category: 'MAIN',
      allowCrossExam: false,
      allowCrossFaculty: true,
      hasPriorityRanking: true,
      subjectSelectionRequired: false,
      ageCalculation: 'AS_OF_NEXT_APRIL_1',
      requireQuestionnaire: false,
      examDates: [{ date: '' }], // Start with one empty date
      paymentMethods: [],
      documents: [],
    },
  });

  const {
    fields: dateFields,
    append: appendDate,
    remove: removeDate,
  } = useFieldArray({
    control: form.control,
    name: 'examDates',
  });

  const {
    fields: docFields,
    append: appendDoc,
    remove: removeDoc,
  } = useFieldArray({
    control: form.control,
    name: 'documents',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount or ID change
  useEffect(() => {
    if (isEdit) {
      fetchExamData(id);
    }
    fetchParentCandidates();
  }, [id]);

  const fetchExamData = async (examId: string) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/exams/${examId}`);
      if (res.ok) {
        // TODO: Update shared-types EntranceExamSchedule to include payment config
        type ExtendedSchedule = EntranceExamSchedule & {
          config?: {
            payment?: {
              methods: string[];
              documents: { name: string; url: string }[];
            };
          };
        };
        const data = (await res.json()) as ExtendedSchedule;

        // Map API data (Nested) to Form data (Flat)
        form.reset({
          name: data.name,
          slug: data.slug,
          // ... (omitted similar lines to keep context but focusing on the type fix)
          academicYear: data.academicYear,
          examType: data.examType,
          category: data.category,
          parentScheduleId: data.parentScheduleId || undefined,
          applicationStart: data.applicationStart,
          applicationEnd: data.applicationEnd,
          announcementDate: data.announcementDate || '',
          procedureDeadline: data.procedureDeadline || '',
          paymentDeadline: data.paymentDeadline || '',

          // Config mapping
          allowCrossExam: data.config?.concurrentRule?.allowCrossExam ?? false,
          allowCrossFaculty: data.config?.concurrentRule?.allowCrossFaculty ?? false,
          maxChoices: data.config?.concurrentRule?.maxChoices,
          hasPriorityRanking: data.config?.concurrentRule?.hasPriorityRanking ?? false,
          subjectSelectionRequired: data.config?.subjectSelection?.required ?? false,
          ageCalculation: data.config?.ageCalculation ?? 'AS_OF_NEXT_APRIL_1',
          requireQuestionnaire: data.config?.requireQuestionnaire ?? false,
          paymentMethods: data.config?.payment?.methods ?? [],
          documents: data.config?.payment?.documents ?? [],

          // Dates mapping
          examDates: data.dates?.map((d: { id: string; date: string; label?: string; capacity?: number }) => ({
            id: d.id,
            date: d.date,
            label: d.label,
            capacity: d.capacity,
          })) || [{ date: '' }],
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchParentCandidates = async () => {
    try {
      // Fetch potential parents (Main exams)
      // Ideally filter by year and category=MAIN in backend
      const res = await fetch(`/api/exams?year=${form.getValues('academicYear')}`);
      if (res.ok) {
        const data = (await res.json()) as EntranceExamSchedule[];
        setParentCandidates(data.filter((e: EntranceExamSchedule) => e.category === 'MAIN' && e.id !== id));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const onSubmit = async (values: ExamFormValues) => {
    setLoading(true);
    try {
      // Reconstruct nested object
      const payload = {
        ...values,
        config: {
          concurrentRule: {
            allowCrossExam: values.allowCrossExam,
            allowCrossFaculty: values.allowCrossFaculty,
            maxChoices: values.maxChoices,
            hasPriorityRanking: values.hasPriorityRanking,
          },
          subjectSelection: {
            required: values.subjectSelectionRequired,
          },
          ageCalculation: values.ageCalculation,
          requireQuestionnaire: values.requireQuestionnaire,
          payment: {
            methods: values.paymentMethods,
            documents: values.documents,
          },
        },
        dates: values.examDates,
      };

      const url = isEdit ? `/api/exams/${id}` : '/api/exams';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Save failed');

      navigate('/admin/master/exams');
    } catch (error) {
      console.error(error);
      alert('保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit && !form.formState.isDirty) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/master/exams')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEdit ? t('modules.exams.editSchedule') : t('modules.exams.newSchedule')}
          </h1>
        </div>
        <Button onClick={form.handleSubmit(onSubmit)} disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          {loading ? t('common.loading') : t('common.save')}
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('modules.exams.sections.basic')}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="academicYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.exams.fields.year')}</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.exams.fields.name')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('modules.exams.fields.namePlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.exams.fields.slug')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('modules.exams.fields.slugPlaceholder')} {...field} />
                    </FormControl>
                    <FormDescription>{t('modules.exams.fields.slugDesc')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="examType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.exams.fields.examType')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value as string}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('modules.exams.fields.examTypePlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ExamTypeSchema.options.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(`modules.exams.types.${type}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('modules.exams.fields.categoryLabel')}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value as string}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('modules.exams.fields.categoryPlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="MAIN">{t('modules.exams.categories.MAIN')}</SelectItem>
                        <SelectItem value="MAKEUP">{t('modules.exams.categories.MAKEUP')}</SelectItem>
                        <SelectItem value="RETAKE">{t('modules.exams.categories.RETAKE')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>追試験の場合は、親となる本試験を選択してください。</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('category') !== 'MAIN' && (
                <div className="space-y-4">
                  <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <History className="h-4 w-4 text-amber-600" />
                    <AlertTitle>予備日程モード</AlertTitle>
                    <AlertDescription className="text-xs mt-1">
                      追試験・再試験は「予備日程」として扱われます。
                      <br />
                      志願者が一覧から直接選択することはできず、本試験からの振替専用となります。
                    </AlertDescription>
                  </Alert>

                  <FormField
                    control={form.control}
                    name="parentScheduleId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('modules.exams.fields.parentSchedule')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="選択してください" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {parentCandidates.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Schedule Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-1">
              <CardHeader>
                <CardTitle>{t('modules.exams.sections.datePeriod')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="applicationStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.exams.fields.applicationStart')}</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="applicationEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.exams.fields.applicationEnd')}</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Separator />
                <FormField
                  control={form.control}
                  name="announcementDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.exams.fields.announcement')}</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="procedureDeadline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.exams.fields.procedureDeadline')}</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paymentDeadline"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('modules.exams.config.fields.paymentDeadline')}</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>{t('modules.exams.config.fields.paymentDeadlineDesc')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="space-y-1">
                  <CardTitle>{t('modules.exams.sections.examDates')}</CardTitle>
                  <p className="text-sm text-muted-foreground">{t('modules.exams.sections.examDatesDesc')}</p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => appendDate({ date: '' })}>
                  <Plus className="w-4 h-4 mr-2" /> {t('modules.exams.dates.add')}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dateFields.map((field, index) => (
                    <div key={field.id} className="flex gap-4 items-end bg-gray-50 p-3 rounded-md">
                      <FormField
                        control={form.control}
                        name={`examDates.${index}.label`}
                        render={({ field }) => (
                          <FormItem className="w-1/4">
                            <FormLabel className="text-xs">{t('modules.exams.dates.fields.label')}</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="Day 1" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`examDates.${index}.date`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-xs">{t('modules.exams.dates.fields.date')}</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`examDates.${index}.capacity`}
                        render={({ field }) => (
                          <FormItem className="w-24">
                            <FormLabel className="text-xs">{t('modules.exams.dates.fields.capacity')}</FormLabel>
                            <FormControl>
                              <Input type="number" {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeDate(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {dateFields.length === 0 && (
                    <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg text-gray-400">
                      <AlertCircle className="w-8 h-8 mb-2" />
                      <p className="text-sm">{t('modules.exams.dates.empty')}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Configurations */}
          <Card>
            <CardHeader>
              <CardTitle>{t('modules.exams.sections.config')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="concurrent" className="w-full">
                <TabsList>
                  <TabsTrigger value="concurrent">{t('modules.exams.config.tabs.concurrent')}</TabsTrigger>
                  <TabsTrigger value="selection">{t('modules.exams.config.tabs.selection')}</TabsTrigger>
                  <TabsTrigger value="payment">{t('modules.exams.config.tabs.payment')}</TabsTrigger>
                </TabsList>
                <div className="mt-4 p-4 border rounded-md">
                  <TabsContent value="concurrent" className="space-y-6">
                    <FormField
                      control={form.control}
                      name="allowCrossExam"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>他入試との併願を許可 (Allow Cross Exam)</FormLabel>
                            <FormDescription>他の入試区分との同時出願を許可する場合にチェックします。</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="allowCrossFaculty"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>学部・学科間の併願を許可 (Cross Faculty)</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="maxChoices"
                      render={({ field }) => (
                        <FormItem className="max-w-xs">
                          <FormLabel>最大併願数 (Max Choices)</FormLabel>
                          <FormControl>
                            <Input type="number" {...field} />
                          </FormControl>
                          <FormDescription>0または空欄で無制限</FormDescription>
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  <TabsContent value="selection" className="space-y-6">
                    <FormField
                      control={form.control}
                      name="ageCalculation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('modules.exams.config.fields.ageCalculation')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="AS_OF_NEXT_APRIL_1">
                                {t('modules.exams.config.fields.ageNextApril')}
                              </SelectItem>
                              <SelectItem value="AT_REGISTRATION">
                                {t('modules.exams.config.fields.ageRegistration')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="requireQuestionnaire"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>{t('modules.exams.config.fields.requireQuestionnaire')}</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  </TabsContent>

                  <TabsContent value="payment" className="space-y-6">
                    <FormField
                      control={form.control}
                      name="paymentMethods"
                      render={() => (
                        <FormItem>
                          <div className="mb-4">
                            <FormLabel className="text-base">
                              {t('modules.exams.config.fields.paymentMethod')}
                            </FormLabel>
                            <FormDescription>Select available payment methods for this exam.</FormDescription>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            {[
                              'CREDIT_CARD',
                              'CONVENIENCE_STORE',
                              'PAY_EASY_ATM',
                              'PAY_EASY_IB',
                              'UNION_PAY',
                              'FLYWIRE',
                            ].map((item) => (
                              <FormField
                                key={item}
                                control={form.control}
                                name="paymentMethods"
                                render={({ field }) => {
                                  return (
                                    <FormItem key={item} className="flex flex-row items-start space-x-3 space-y-0">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(item)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, item])
                                              : field.onChange(field.value?.filter((value) => value !== item));
                                          }}
                                        />
                                      </FormControl>
                                      <FormLabel className="font-normal">
                                        {t(`modules.exams.config.fields.paymentMethods.${item}`)}
                                      </FormLabel>
                                    </FormItem>
                                  );
                                }}
                              />
                            ))}
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <FormLabel className="text-base">{t('modules.exams.config.fields.documents')}</FormLabel>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => appendDoc({ name: '', url: '' })}
                        >
                          <Plus className="w-4 h-4 mr-2" /> {t('modules.exams.dates.add')}
                        </Button>
                      </div>

                      {docFields.map((field, index) => (
                        <div
                          key={field.id}
                          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-3 border rounded-md bg-gray-50 relative"
                        >
                          <FormField
                            control={form.control}
                            name={`documents.${index}.name`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">
                                  {t('modules.exams.config.fields.documentName')}
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('modules.exams.config.fields.documentNamePlaceholder')}
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`documents.${index}.url`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-xs">
                                  {t('modules.exams.config.fields.documentUrl')}
                                </FormLabel>
                                <div className="flex gap-2">
                                  <FormControl>
                                    <Input
                                      placeholder={t('modules.exams.config.fields.documentUrlPlaceholder')}
                                      {...field}
                                    />
                                  </FormControl>
                                  {field.value && (
                                    <a
                                      href={field.value}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2 text-blue-600 hover:text-blue-800"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                    </a>
                                  )}
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-1 right-1 text-red-500 hover:text-red-700"
                            onClick={() => removeDoc(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {docFields.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-4 border border-dashed rounded">
                          No documents attached.
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </form>
      </Form>
    </div>
  );
}
